export const retouchToolVertexShader = `#version 300 es

in vec2 a_position;
in vec2 a_texCoord;

uniform mat3 u_camViewProjection;
uniform mat3 u_model;

out vec2 v_texCoord;

void main() {
    mat3 u_matrix = u_camViewProjection * u_model;
    vec3 position = u_matrix * vec3(a_position, 1);
    gl_Position = vec4(position, 1);
    
    v_texCoord = a_texCoord;
}
`;

export const retouchToolFragmentShader = `#version 300 es

precision highp float;

in vec2 v_texCoord;

uniform sampler2D u_texture0;
uniform sampler2D u_texture1;

// Texture size in pixels
uniform vec2 u_size;

// 0..1 strength of retouch
uniform float u_strength;

// Sampling radius in pixels (ring sampling)
uniform float u_sampleRadius;

out vec4 outColor;

float luma(vec3 rgb) {
  return dot(rgb, vec3(0.299, 0.587, 0.114));
}

// March outwards along direction until we sample outside the mask.
vec3 sampleOutsideMask(vec2 uv, vec2 dir, vec2 texel, float radiusPx) {
  vec2 stepUv = dir * texel;
  vec2 offset = dir * texel * radiusPx;

  // Keep the loop small to avoid GPU stalls/hangs.
  // In most cases radiusPx already lands outside the mask.
  for (int i = 0; i < 12; i++) {
    float m = texture(u_texture1, uv + offset).a;
    if (m < 0.01) break;
    offset += stepUv;
  }

  return texture(u_texture0, uv + offset).rgb;
}

vec4 ringAverageTrimmed(vec2 uv, vec2 texel, float radiusPx) {
  // 8-direction ring sampling (excludes the center sample).
  // Use a trimmed mean: discard darkest + brightest sample by luma
  // for robustness to outliers (dust specks, highlights).

  vec2 dir0 = vec2( 1.0,  0.0);
  vec2 dir1 = vec2(-1.0,  0.0);
  vec2 dir2 = vec2( 0.0,  1.0);
  vec2 dir3 = vec2( 0.0, -1.0);

  vec2 dir4 = normalize(vec2( 1.0,  1.0));
  vec2 dir5 = normalize(vec2(-1.0,  1.0));
  vec2 dir6 = normalize(vec2( 1.0, -1.0));
  vec2 dir7 = normalize(vec2(-1.0, -1.0));

  vec3 c0 = sampleOutsideMask(uv, dir0, texel, radiusPx);
  vec3 c1 = sampleOutsideMask(uv, dir1, texel, radiusPx);
  vec3 c2 = sampleOutsideMask(uv, dir2, texel, radiusPx);
  vec3 c3 = sampleOutsideMask(uv, dir3, texel, radiusPx);
  vec3 c4 = sampleOutsideMask(uv, dir4, texel, radiusPx);
  vec3 c5 = sampleOutsideMask(uv, dir5, texel, radiusPx);
  vec3 c6 = sampleOutsideMask(uv, dir6, texel, radiusPx);
  vec3 c7 = sampleOutsideMask(uv, dir7, texel, radiusPx);

  vec3 sum = c0 + c1 + c2 + c3 + c4 + c5 + c6 + c7;

  vec3 minC = c0;
  vec3 maxC = c0;
  float l0 = luma(c0);
  float minL = l0;
  float maxL = l0;

  float l1 = luma(c1);
  if (l1 < minL) { minL = l1; minC = c1; }
  if (l1 > maxL) { maxL = l1; maxC = c1; }

  float l2 = luma(c2);
  if (l2 < minL) { minL = l2; minC = c2; }
  if (l2 > maxL) { maxL = l2; maxC = c2; }

  float l3 = luma(c3);
  if (l3 < minL) { minL = l3; minC = c3; }
  if (l3 > maxL) { maxL = l3; maxC = c3; }

  float l4 = luma(c4);
  if (l4 < minL) { minL = l4; minC = c4; }
  if (l4 > maxL) { maxL = l4; maxC = c4; }

  float l5 = luma(c5);
  if (l5 < minL) { minL = l5; minC = c5; }
  if (l5 > maxL) { maxL = l5; maxC = c5; }

  float l6 = luma(c6);
  if (l6 < minL) { minL = l6; minC = c6; }
  if (l6 > maxL) { maxL = l6; maxC = c6; }

  float l7 = luma(c7);
  if (l7 < minL) { minL = l7; minC = c7; }
  if (l7 > maxL) { maxL = l7; maxC = c7; }

  sum -= minC;
  sum -= maxC;
  vec3 avgRgb = sum / 6.0;
  float range = maxL - minL; // neighborhood contrast proxy

  // In high-contrast / textured regions, pure averaging tends to "smudge".
  // Pick a representative neighbor sample close to the trimmed mean to preserve local texture.
  float avgL = luma(avgRgb);
  vec3 bestRgb = c0;
  float bestDiff = abs(luma(c0) - avgL);

  float diff1 = abs(l1 - avgL);
  if (diff1 < bestDiff) { bestDiff = diff1; bestRgb = c1; }
  float diff2 = abs(l2 - avgL);
  if (diff2 < bestDiff) { bestDiff = diff2; bestRgb = c2; }
  float diff3 = abs(l3 - avgL);
  if (diff3 < bestDiff) { bestDiff = diff3; bestRgb = c3; }
  float diff4 = abs(l4 - avgL);
  if (diff4 < bestDiff) { bestDiff = diff4; bestRgb = c4; }
  float diff5 = abs(l5 - avgL);
  if (diff5 < bestDiff) { bestDiff = diff5; bestRgb = c5; }
  float diff6 = abs(l6 - avgL);
  if (diff6 < bestDiff) { bestDiff = diff6; bestRgb = c6; }
  float diff7 = abs(l7 - avgL);
  if (diff7 < bestDiff) { bestDiff = diff7; bestRgb = c7; }

  // Separate "texture" from "smooth but high-contrast gradients" (highlights).
  // If most of the contrast comes from a coherent gradient, prefer averaging to avoid blotches.
  float gradMag = length(vec2(l0 - l1, l2 - l3));
  float gradRatio = gradMag / (range + 1e-5); // 1 = mostly gradient, 0 = mostly texture/noise

  float textureGate = smoothstep(0.10, 0.25, range) * smoothstep(0.25, 0.75, 1.0 - gradRatio);
  vec3 outRgb = mix(avgRgb, bestRgb, textureGate * 0.75);

  // Store "texturedness" in alpha for multi-scale blending decisions.
  return vec4(outRgb, textureGate);
}

void main() {
  float mask = texture(u_texture1, v_texCoord).a;
  if (mask <= 0.001) {
    outColor = texture(u_texture0, v_texCoord);
    return;
  }

  vec4 original = texture(u_texture0, v_texCoord);

  float sampleRadiusPx = max(1.0, u_sampleRadius);
  vec2 texel = vec2(1.0, 1.0) / u_size;
  vec4 avg1 = ringAverageTrimmed(v_texCoord, texel, sampleRadiusPx);
  vec4 avg2 = ringAverageTrimmed(v_texCoord, texel, sampleRadiusPx * 1.6);

  // Use "texturedness" to decide multi-scale mix.
  float textured = max(avg1.a, avg2.a); // 0 smooth/gradient -> 1 textured
  float envGate = 1.0 - textured; // 1 smooth areas, 0 textured/edges
  float wOuter = 0.55 * envGate;
  vec3 avg = mix(avg1.rgb, avg2.rgb, wOuter);

  // Stronger effect when the pixel differs from its environment (dust/speck),
  // but reduce smearing when it already matches.
  float diffL = abs(luma(avg) - luma(original.rgb));
  float diffGate = smoothstep(0.02, 0.10, diffL);

  float t = clamp(u_strength, 0.0, 1.0) * clamp(mask, 0.0, 1.0) * mix(0.35, 1.0, diffGate);
  vec3 healed = mix(original.rgb, avg, t);

  // Keep original alpha to avoid edge halos on transparent backgrounds.
  outColor = vec4(healed, original.a);
}
` as const;
